import type { MonospaceRole, MonospaceUserRole, RelationOperationToOneInCreate, RelationOperationToOneInUpdate } from '#layers/data-model';
import { RStoreSchema } from '#imports';
import { isNullishOrEmpty } from '#layers/base/utils/iterable';
import { omit } from '#layers/base/utils/object';
import { isConnectToOne, isDisconnectToOne } from '#layers/data-model';
import { useProjectFetch } from '~/composables/use-api';

export const Role = RStoreSchema.withItemType<MonospaceRole>().defineCollection({
	name: 'MonospaceRole',
	scopeId: 'monospace-default',
	meta: {
		defaultFields: ['id', 'name', 'parentId', 'isPublic'],
		fetchOnCreate: true,
		// TODO clean endpoint up once system routes land
		endpoint: {
			single: {
				create: 'roles',
				update: 'roles',
				delete: 'roles',
				// read goes through normal items endpoint
			},
			many: 'roles',
		},
		transformBeforeCreate: (role: MonospaceRole & { parent?: RelationOperationToOneInCreate }) => {
			// TODO this needs to be adapted in the engine to accept `parent` relational input

			const roleWithoutId = omit(role, ['id']);

			if (role.parent && isConnectToOne(role.parent)) {
				return {
					...roleWithoutId,
					parent: undefined,
					parentId: role.parent._connect.key.id,
				};
			}

			return roleWithoutId;
		},
		transformBeforeUpdate: (role: MonospaceRole & { parent?: RelationOperationToOneInUpdate }) => {
			// TODO this needs to be adapted in the engine to accept `parent` relational input
			if (isNullishOrEmpty(role.parent)) return role;

			// only look at last operation since that one takes precedence
			const lastOp = role.parent.at(-1)!;

			if (isConnectToOne(lastOp)) {
				return {
					...role,
					parent: undefined,
					parentId: lastOp._connect.key.id,
				};
			}
			else if (isDisconnectToOne(lastOp)) {
				return {
					...role,
					parent: undefined,
					parentId: null,
				};
			}

			return role;
		},
		useProjectSlug: true,
	},
	hooks: {
		deleteMany: async ({ keys }) => {
			const fetch = useProjectFetch();

			const results = await Promise.allSettled(keys.map((key) =>
				fetch(`roles/${key}`, {
					method: 'DELETE',
				}),
			));

			if (results.some((result) => result.status === 'rejected')) {
				throw new Error('Failed to delete some roles');
			}
		},
	},
});

export const RoleClosure = RStoreSchema.withItemType<{ ancestorId: string; descendantId: string }>().defineCollection({
	name: 'MonospaceRoleClosure',
	scopeId: 'monospace-default',
	getKey: (item) => `${item.ancestorId}__${item.descendantId}`,
	meta: {
		defaultFields: ['ancestorId', 'descendantId'],
		useProjectSlug: true,
	},
});

export const UserRole = RStoreSchema.withItemType<MonospaceUserRole>().defineCollection({
	name: 'MonospaceUserRole',
	scopeId: 'monospace-default',
	getKey: (item) => `${item.userId}__${item.roleId}`,
	meta: {
		defaultFields: ['userId', 'roleId'],
		useProjectSlug: true,
	},
});
